"use client";

import { motion, useTransform } from "framer-motion";
import type { MotionValue } from "framer-motion";
import * as React from "react";

import { GridCross, GridTee } from "@/components/ui/grid-connector";
import { cn } from "@/lib/utils";

export interface StepTabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

interface StepTabsProps {
  tabs: readonly StepTabItem[];
  active: number;
  progress: MotionValue<number>;
  onSelect: (index: number) => void;
  /** Accessible name for the tablist. */
  ariaLabel?: string;
  className?: string;
  /** Desktop drops left border — a fade line is the rail edge (homepage pattern). */
  hideLeftBorderOnDesktop?: boolean;
  /** Show numeric cells for inactive tabs on mobile (homepage pattern). */
  collapseInactiveOnMobile?: boolean;
  /** Top rail junction marks — homepage uses a single cross; tools uses ⊤ both sides. */
  topCornerConnectors?: "cross-left" | "tee-both";
}

/**
 * Shared progress-tab bar used by the homepage product preview and the tools
 * hub. Progress is driven externally via a 0–1 MotionValue (video time, fixed
 * timer, etc.).
 */
export function StepTabs({
  tabs,
  active,
  progress,
  onSelect,
  ariaLabel = "Steps",
  className,
  hideLeftBorderOnDesktop = false,
  collapseInactiveOnMobile = false,
  topCornerConnectors = "cross-left",
}: StepTabsProps) {
  const fillScale = useTransform(progress, (value) =>
    Math.min(1, Math.max(0, value))
  );

  return (
    <div
      className={cn(
        "relative flex overflow-visible border border-[#e5e6e6] bg-white",
        hideLeftBorderOnDesktop && "lg:border-l-0",
        className
      )}
      role="tablist"
      aria-label={ariaLabel}
    >
      {topCornerConnectors === "tee-both" ? (
        <>
          <GridTee className="text-grey-950 pointer-events-none absolute -top-[0.5px] -left-[3.5px] z-30" />
          <GridTee className="text-grey-950 pointer-events-none absolute -top-[0.5px] -right-[3.5px] z-30" />
        </>
      ) : (
        <GridCross
          corner="top-left"
          className="absolute -top-[0.5px] -left-[0.5px] z-30 hidden lg:block"
        />
      )}
      {tabs.map((tab, i) => {
        const isActive = active === i;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(i)}
            className={cn(
              "text-grey-950 hover:text-grey-500 relative flex h-12 items-center justify-center overflow-visible border-l border-[#e5e6e6] px-2 text-center text-[13px] leading-[1.5] tracking-[-0.28px] transition-colors lg:h-[62px] lg:flex-1 lg:gap-3 lg:px-0 lg:text-[14px]",
              i === 0 && "border-l-0",
              collapseInactiveOnMobile &&
                (isActive
                  ? "flex-1 gap-2 lg:gap-3"
                  : "w-12 shrink-0 lg:w-auto lg:flex-1")
            )}
          >
            {tab.icon && (
              <span
                className={cn(
                  "shrink-0 items-center justify-center",
                  collapseInactiveOnMobile
                    ? isActive
                      ? "inline-flex size-6"
                      : "hidden"
                    : "hidden lg:inline-flex lg:size-6"
                )}
                aria-hidden
              >
                {tab.icon}
              </span>
            )}
            <span
              className={cn(
                collapseInactiveOnMobile ? "lg:inline" : "inline",
                collapseInactiveOnMobile && !isActive && "hidden lg:inline"
              )}
            >
              {tab.label}
            </span>
            {collapseInactiveOnMobile && (
              <span className={cn("lg:hidden", isActive && "hidden")}>
                {String(i + 1).padStart(2, "0")}
              </span>
            )}
            {isActive && (
              <motion.span
                aria-hidden
                className="bg-grey-950 absolute inset-x-0 bottom-0 block h-px w-full origin-left will-change-transform"
                style={{ scaleX: fillScale }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
